import { useEffect, type RefObject } from "react";
import type { Density } from "@/components/readwise/HighlightFilters";

const isTypingTarget = (el: Element | null) => {
  if (!el) return false;
  const tag = el.tagName;
  return (
    tag === "INPUT" ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    (el as HTMLElement).isContentEditable
  );
};

type Args = {
  effectiveDensity: Density;
  pagedLength: number;
  page: number;
  rowRefs: RefObject<(HTMLButtonElement | null)[]>;
  searchInputRef: RefObject<HTMLInputElement>;
  allExpandedOnPage: boolean;
  expandAllOnPage: () => void;
  collapseAllOnPage: () => void;
};

/** Wires j/k arrow navigation + `/` and `e` shortcuts on the highlights page. */
export const useReadwiseKeyboardNav = ({
  effectiveDensity,
  pagedLength,
  page,
  rowRefs,
  searchInputRef,
  allExpandedOnPage,
  expandAllOnPage,
  collapseAllOnPage,
}: Args) => {
  useEffect(() => {
    if (effectiveDensity !== "compact" || pagedLength === 0) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(document.activeElement)) return;
      const isDown = e.key === "j" || e.key === "ArrowDown";
      const isUp = e.key === "k" || e.key === "ArrowUp";
      if (!isDown && !isUp) return;
      const refs = rowRefs.current ?? [];
      if (refs.length === 0) return;
      const current = refs.indexOf(document.activeElement as HTMLButtonElement);
      let target: number;
      if (current === -1) {
        target = isDown ? 0 : refs.length - 1;
      } else {
        target = isDown
          ? Math.min(refs.length - 1, current + 1)
          : Math.max(0, current - 1);
      }
      const el = refs[target];
      if (!el) return;
      e.preventDefault();
      el.focus();
      el.scrollIntoView({ block: "nearest" });
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [effectiveDensity, pagedLength, page, rowRefs]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      if (isTypingTarget(document.activeElement)) return;
      if (e.key === "/") {
        e.preventDefault();
        const el = searchInputRef.current;
        if (el) {
          el.focus();
          el.select();
          el.scrollIntoView({ block: "nearest" });
        }
        return;
      }
      if (e.key === "e" || e.key === "E") {
        if (effectiveDensity !== "compact" || pagedLength === 0) return;
        e.preventDefault();
        if (allExpandedOnPage) collapseAllOnPage();
        else expandAllOnPage();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [effectiveDensity, pagedLength, allExpandedOnPage, collapseAllOnPage, expandAllOnPage, searchInputRef]);
};
